import React, { useState } from 'react';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Tooltip from '@mui/material/Tooltip';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { Switch } from '@mui/material';
import { EditNotifications } from '@mui/icons-material';
import type { Category } from '../../../types/cards';
import { StyledCategoryCard } from '../ThemeComponent';

interface CategoryCardProps {
	category: Category;
	handelEdit: (category: any) => void;
	handleDelete: () => void;
	handleToggleStatus: (id: any, status: any) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
	category,
	handelEdit,
	handleDelete,
	handleToggleStatus,
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const truncateDescription = (text: any, wordCount: any = 10) => {
		if (!text) return '';
		const words = text.split(' ');
		if (words.length <= wordCount) return text;
		return `${words.slice(0, wordCount).join(' ')}...`;
	};

	return (
		<StyledCategoryCard>
			<div style={{ position: 'relative' }}>
				<CardMedia
					component="img"
					height="140"
					image={
						typeof category?.categoryImage === 'string'
							? category.categoryImage
							: category?.categoryImage instanceof File
								? URL.createObjectURL(category.categoryImage)
								: '/ccc faavicon/apple-icon.png'
					}
					alt={category?.name}
					sx={{
						objectFit: 'contain',
						filter: category.status == 0 ? 'grayscale(100%) opacity(0.5)' : 'none',
						transition: 'filter 0.3s',
					}}
				/>

				<Tooltip title="Actions" arrow>
					<IconButton
						aria-label="more"
						aria-controls="long-menu"
						aria-haspopup="true"
						onClick={handleMenuClick}
						style={{
							position: 'absolute',
							top: 8,
							right: 8,
							backgroundColor: 'rgba(255, 255, 255, 0.8)',
							backdropFilter: 'blur(4px)',
						}}
					>
						{anchorEl ? <MoreVertIcon /> : <MoreHorizIcon />}
					</IconButton>
				</Tooltip>
			</div>

			<CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
				<Typography
					gutterBottom
					component="div"
					sx={{
						fontFamily: 'Poppins',
						fontWeight: '700',
						fontSize: '12px',
						lineHeight: 1.2,
						mb: 1,
					}}
				>
					{category.name}
				</Typography>

				{category?.description && (
					<Tooltip title={category.description} arrow placement="top">
						<Typography
							variant="body1"
							color="text.secondary"
							sx={{
								fontFamily: 'Poppins',
								fontWeight: '500',
								fontSize: '12px',
								color: '#658E81',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								mb: 1,
								cursor: 'pointer',
							}}
						>
							{truncateDescription(category.description)}
						</Typography>
					</Tooltip>
				)}

				<Menu
					id="long-menu"
					anchorEl={anchorEl}
					open={open}
					onClose={handleMenuClose}
					PaperProps={{
						style: {
							maxHeight: 48 * 4.5,
							width: '20ch',
							borderRadius: '8px',
						},
					}}
				>
					<MenuItem>
						<Switch
							checked={category.status === 1}
							onChange={() => handleToggleStatus(category._id, category)}
							color="primary"
							// disabled={updatingId === sc._id}
						/>
					</MenuItem>
					<MenuItem
						onClick={() => {
							handelEdit(category);
							handleMenuClose();
						}}
						sx={{ fontFamily: 'Poppins' }}
					>
						<EditNotifications fontSize="small" sx={{ mr: 1 }} /> Edit
					</MenuItem>
					<MenuItem
						onClick={() => {
							handleDelete();
							handleMenuClose();
						}}
						sx={{ fontFamily: 'Poppins', color: 'error.main' }}
					>
						<GridDeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
					</MenuItem>
				</Menu>
			</CardContent>
		</StyledCategoryCard>
	);
};

export default CategoryCard;
